import { useEffect } from "react";
import { TokenService } from "../../services/token.service";

/**
 * Paths that do not require a valid internal JWT (match public routes in AppRoutes).
 */
function isPublicPath(pathname: string): boolean {
  const path = pathname || "/";
  if (path === "/" || path === "") return true;
  if (path === "/welcome") return true;
  if (path === "/faq") return true;
  if (path === "/privacy-policy") return true;
  if (path === "/terms-of-service") return true;
  if (path === "/cookie-policy") return true;
  if (path === "/accessibility-statement") return true;
  if (path.startsWith("/auth/callback")) return true;
  return false;
}

/**
 * After logout, the browser back button can restore an authenticated page from the
 * back-forward cache (bfcache) even though tokens were cleared — the UI looks logged in
 * until React re-runs. This component forces a hard redirect to the welcome page when
 * a cached protected view is shown without a valid token.
 */
export function BfcacheAuthRecovery(): null {
  useEffect(() => {
    const redirectIfUnauthenticatedOnProtectedRoute = (): void => {
      if (TokenService.isInternalTokenValid()) return;

      const pathname = window.location.pathname;
      if (isPublicPath(pathname)) return;

      window.location.replace("/");
    };

    const onPageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) return;
      redirectIfUnauthenticatedOnProtectedRoute();
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState !== "visible") return;
      redirectIfUnauthenticatedOnProtectedRoute();
    };

    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return null;
}
